/**
 * RuleFormModal Component
 *
 * Modal form for creating and editing classification rules.
 * Includes pattern testing with live preview of matching transactions.
 *
 * Features:
 * - Create and Edit modes
 * - Pattern testing with debounce
 * - Validation feedback for invalid regex
 * - Account selection via AccountTreeSelect
 * - Priority and owner default settings
 * - Loading state during save and test
 * - Direction filter for incoming/outgoing transactions
 * - RegexPatternBuilder for visual pattern creation
 * - TransactionPreview for testing patterns
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import {
  X,
  Save,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  FileText,
  CheckCircle,
  Clock,
} from 'lucide-react';
import type {
  ClassificationRule,
  CreateRuleRequest,
  UpdateRuleRequest,
  Account,
  Owner,
  RuleTestMatch,
  RuleDirection,
  TransactionDirection,
} from '@/types/accounting';
import { AccountTreeSelect } from '../../shared/AccountTreeSelect';
import { RegexPatternBuilder } from './RegexPatternBuilder';
import { TransactionPreview, type TestResult } from './TransactionPreview';

/**
 * Source transaction info for pre-filling form fields
 */
export interface SourceTransaction {
  id: number;
  description: string;
  direction: TransactionDirection;
  owner: Owner;
}

export interface RuleFormModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Callback when a rule is created, returns the new rule ID */
  onCreate: (data: CreateRuleRequest) => Promise<number | void>;
  /** Callback when a rule is updated */
  onUpdate: (id: number, data: UpdateRuleRequest) => Promise<void>;
  /** Callback to test a pattern (direction is undefined for 'all') */
  onTestPattern: (pattern: string, direction?: string) => Promise<{ matchingTransactions: RuleTestMatch[]; matchCount: number }>;
  /** Rule to edit (null for create mode) */
  rule: ClassificationRule | null;
  /** Flat list of all accounts */
  accounts: Account[];
  /** Hierarchical tree structure for display */
  accountTree: Account[];
  /** Source transaction for pre-filling form fields when creating from transaction */
  sourceTransaction?: SourceTransaction;
  /** Initial target account ID to pre-select */
  initialTargetAccountId?: number;
  /** Callback to create rule and classify source transaction */
  onCreateAndClassify?: (ruleId: number) => Promise<void>;
}

interface FormData {
  name: string;
  pattern: string;
  targetAccountId: number | null;
  ownerDefault: Owner;
  priority: number;
  direction: RuleDirection;
}

/**
 * Validate if a string is a valid regex pattern
 */
function isValidRegex(pattern: string): boolean {
  try {
    new RegExp(pattern);
    return true;
  } catch {
    return false;
  }
}

/**
 * RuleFormModal for creating and editing classification rules.
 *
 * @example
 * <RuleFormModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   onCreate={handleCreate}
 *   onUpdate={handleUpdate}
 *   onTestPattern={testPattern}
 *   rule={editingRule}
 *   accounts={accounts}
 *   accountTree={accountTree}
 * />
 */
/**
 * Format relative time for rule stats
 */
function formatLastMatchedAt(dateString: string | null | undefined): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;

  // Format as date for older matches
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

export function RuleFormModal({
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  onTestPattern,
  rule,
  accounts,
  accountTree,
  sourceTransaction,
  initialTargetAccountId,
  onCreateAndClassify,
}: RuleFormModalProps) {
  const isEditMode = rule !== null;
  const isCreatingFromTransaction = !isEditMode && sourceTransaction !== undefined;

  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    pattern: '',
    targetAccountId: null,
    ownerDefault: 'family',
    priority: 5,
    direction: 'all',
  });

  // Example text for pattern builder (from transaction preview)
  const [exampleText, setExampleText] = useState('');

  // Toggle state for transaction preview
  const [showPreview, setShowPreview] = useState(false);

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Reset form when rule changes or modal opens
  useEffect(() => {
    if (isOpen) {
      if (rule) {
        // Edit mode: load existing rule data
        setFormData({
          name: rule.name,
          pattern: rule.pattern,
          targetAccountId: rule.targetAccountId,
          ownerDefault: rule.ownerDefault ?? 'family',
          priority: rule.priority,
          direction: rule.direction ?? 'all',
        });
        setExampleText('');
      } else if (sourceTransaction) {
        // Create mode from transaction: pre-fill from source transaction
        setFormData({
          name: '',
          pattern: '',
          targetAccountId: initialTargetAccountId ?? null,
          ownerDefault: sourceTransaction.owner,
          priority: 5,
          direction: sourceTransaction.direction,
        });
        // Pre-fill example text with transaction description
        setExampleText(sourceTransaction.description);
      } else {
        // Create mode: reset to defaults
        setFormData({
          name: '',
          pattern: '',
          targetAccountId: initialTargetAccountId ?? null,
          ownerDefault: 'family',
          priority: 5,
          direction: 'all',
        });
        setExampleText('');
      }
      setShowPreview(false);
      setFormError(null);
    }
  }, [isOpen, rule, sourceTransaction, initialTargetAccountId]);

  // Validate pattern
  const isPatternValid = useMemo(() => {
    return formData.pattern.length === 0 || isValidRegex(formData.pattern);
  }, [formData.pattern]);

  // Form validation
  const canSubmit = useMemo(() => {
    return (
      formData.name.trim().length > 0 &&
      formData.pattern.trim().length > 0 &&
      isPatternValid &&
      formData.targetAccountId !== null &&
      !isSaving
    );
  }, [formData, isPatternValid, isSaving]);

  // Handle field changes
  const handleChange = useCallback(
    (field: keyof FormData, value: string | number | null | RuleDirection) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      setFormError(null);
    },
    []
  );

  // Handle pattern change from RegexPatternBuilder
  const handlePatternChange = useCallback((newPattern: string) => {
    handleChange('pattern', newPattern);
  }, [handleChange]);

  // Handle using a transaction description as example
  const handleUseAsExample = useCallback((description: string) => {
    setExampleText(description);
  }, []);

  // Wrap onTestPattern to handle direction
  const handleTestPatternForPreview = useCallback(
    async (pattern: string, direction?: string): Promise<TestResult> => {
      const result = await onTestPattern(pattern, direction);
      return {
        matchingTransactions: result.matchingTransactions.map((t) => ({
          ...t,
          amount: (t as { amount?: number }).amount ?? 0,
          direction: (t as { direction?: 'incoming' | 'outgoing' }).direction ?? 'outgoing',
        })),
        matchCount: result.matchCount,
      };
    },
    [onTestPattern]
  );

  // Handle form submit
  const handleSubmit = useCallback(
    async (e: React.FormEvent, shouldClassify: boolean = false) => {
      e.preventDefault();

      if (!canSubmit || formData.targetAccountId === null) return;

      setIsSaving(true);
      setFormError(null);

      try {
        if (isEditMode && rule) {
          const updateData: UpdateRuleRequest = {
            name: formData.name.trim(),
            pattern: formData.pattern.trim(),
            targetAccountId: formData.targetAccountId,
            ownerDefault: formData.ownerDefault,
            priority: formData.priority,
            direction: formData.direction,
          };
          await onUpdate(rule.id, updateData);
        } else {
          const createData: CreateRuleRequest = {
            name: formData.name.trim(),
            pattern: formData.pattern.trim(),
            targetAccountId: formData.targetAccountId,
            ownerDefault: formData.ownerDefault,
            priority: formData.priority,
            direction: formData.direction,
          };
          const newRuleId = await onCreate(createData);

          // If shouldClassify and we have the callback, call it with the new rule ID
          if (shouldClassify && onCreateAndClassify && newRuleId !== undefined) {
            await onCreateAndClassify(newRuleId);
          }
        }
        onClose();
      } catch (error) {
        setFormError(
          error instanceof Error ? error.message : 'Failed to save rule'
        );
      } finally {
        setIsSaving(false);
      }
    },
    [canSubmit, formData, isEditMode, rule, onCreate, onUpdate, onClose, onCreateAndClassify]
  );

  // Handle create and classify button click
  const handleCreateAndClassify = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      // Create a synthetic form event
      const syntheticEvent = { preventDefault: () => {} } as React.FormEvent;
      await handleSubmit(syntheticEvent, true);
    },
    [handleSubmit]
  );

  // Handle modal close
  const handleClose = useCallback(() => {
    if (!isSaving) {
      onClose();
    }
  }, [isSaving, onClose]);

  // Handle backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose]
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
      data-testid="rule-form-modal-backdrop"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="rule-form-modal"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEditMode ? 'Edit Rule' : 'Create Rule'}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSaving}
            className={clsx(
              'p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100',
              'focus:outline-none focus:ring-2 focus:ring-blue-500',
              isSaving && 'cursor-not-allowed opacity-50'
            )}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-4 space-y-4">
            {/* Source Transaction Banner */}
            {isCreatingFromTransaction && sourceTransaction && (
              <div
                className="p-3 bg-blue-50 border border-blue-200 rounded-md flex items-start gap-2"
                data-testid="source-transaction-banner"
              >
                <FileText
                  size={16}
                  className="text-blue-600 flex-shrink-0 mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-blue-800">
                    Creating from transaction
                  </p>
                  <p
                    className="text-sm text-blue-700 truncate"
                    title={sourceTransaction.description}
                  >
                    {sourceTransaction.description}
                  </p>
                </div>
              </div>
            )}

            {/* Rule Stats (Edit Mode) */}
            {isEditMode && rule && (
              <div
                className="p-3 bg-gray-50 border border-gray-200 rounded-md flex items-center gap-4"
                data-testid="rule-stats-banner"
              >
                <div className="flex items-center gap-1.5">
                  <CheckCircle size={14} className="text-green-600" />
                  <span className="text-sm text-gray-700">
                    <span className="font-medium">{rule.matchCount}</span>{' '}
                    {rule.matchCount === 1 ? 'match' : 'matches'}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock size={14} className="text-gray-500" />
                  <span className="text-sm text-gray-600">
                    Last match: {formatLastMatchedAt(rule.lastMatchedAt)}
                  </span>
                </div>
              </div>
            )}

            {/* Form Error */}
            {formError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
                <AlertCircle
                  size={16}
                  className="text-red-600 flex-shrink-0 mt-0.5"
                />
                <p className="text-sm text-red-700">{formError}</p>
              </div>
            )}

            {/* Name */}
            <div>
              <label
                htmlFor="rule-name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Rule Name <span className="text-red-500">*</span>
              </label>
              <input
                id="rule-name"
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Grocery Stores"
                disabled={isSaving}
                className={clsx(
                  'w-full px-3 py-2 text-sm border rounded-md',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  'disabled:bg-gray-50 disabled:cursor-not-allowed',
                  'border-gray-300'
                )}
                data-testid="rule-name-input"
              />
            </div>

            {/* Direction */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Applies To
              </label>
              <div className="flex gap-3" data-testid="direction-radio-group">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="direction"
                    value="all"
                    checked={formData.direction === 'all'}
                    onChange={() => handleChange('direction', 'all')}
                    disabled={isSaving}
                    className="text-blue-600 focus:ring-blue-500"
                    data-testid="direction-all"
                  />
                  <span className="text-sm text-gray-700">All</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="direction"
                    value="outgoing"
                    checked={formData.direction === 'outgoing'}
                    onChange={() => handleChange('direction', 'outgoing')}
                    disabled={isSaving}
                    className="text-blue-600 focus:ring-blue-500"
                    data-testid="direction-outgoing"
                  />
                  <span className="text-sm text-gray-700">Outgoing</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    expenses
                  </span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="direction"
                    value="incoming"
                    checked={formData.direction === 'incoming'}
                    onChange={() => handleChange('direction', 'incoming')}
                    disabled={isSaving}
                    className="text-blue-600 focus:ring-blue-500"
                    data-testid="direction-incoming"
                  />
                  <span className="text-sm text-gray-700">Incoming</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    income
                  </span>
                </label>
              </div>
            </div>

            {/* Pattern */}
            <div>
              <label
                htmlFor="rule-pattern"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Pattern (Regex) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="rule-pattern"
                  type="text"
                  value={formData.pattern}
                  onChange={(e) => handleChange('pattern', e.target.value)}
                  placeholder="e.g., carrefour|leclerc|auchan"
                  disabled={isSaving}
                  className={clsx(
                    'w-full px-3 py-2 text-sm font-mono border rounded-md',
                    'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                    'disabled:bg-gray-50 disabled:cursor-not-allowed',
                    !isPatternValid
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300'
                  )}
                  data-testid="rule-pattern-input"
                />
                {!isPatternValid && (
                  <p className="mt-1 text-sm text-red-600">
                    Invalid regular expression
                  </p>
                )}
              </div>
            </div>

            {/* Regex Pattern Builder */}
            <RegexPatternBuilder
              pattern={formData.pattern}
              onPatternChange={handlePatternChange}
              exampleText={exampleText}
              editableExample={isEditMode}
              onExampleTextChange={setExampleText}
            />

            {/* Transaction Preview Toggle */}
            <div>
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                disabled={!formData.pattern.trim() || !isPatternValid}
                className={clsx(
                  'inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md w-full justify-center',
                  'bg-gray-100 text-gray-700 hover:bg-gray-200',
                  'focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
                data-testid="preview-toggle-button"
              >
                {showPreview ? (
                  <>
                    <ChevronUp size={16} />
                    Hide Matching Transactions
                  </>
                ) : (
                  <>
                    <ChevronDown size={16} />
                    Preview Matching Transactions
                  </>
                )}
              </button>
            </div>

            {/* Transaction Preview */}
            {showPreview && formData.pattern.trim() && isPatternValid && (
              <TransactionPreview
                pattern={formData.pattern}
                direction={formData.direction}
                onTestPattern={handleTestPatternForPreview}
                onUseAsExample={handleUseAsExample}
              />
            )}

            {/* Target Account */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Account <span className="text-red-500">*</span>
              </label>
              <AccountTreeSelect
                accounts={accounts}
                accountTree={accountTree}
                value={formData.targetAccountId}
                onChange={(id) => handleChange('targetAccountId', id)}
                placeholder="Select expense account..."
                filterTypes={['expense']}
                disabled={isSaving}
                data-testid="target-account-select"
              />
            </div>

            {/* Owner Default */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Owner
              </label>
              <div className="flex gap-4">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="ownerDefault"
                    value="family"
                    checked={formData.ownerDefault === 'family'}
                    onChange={() => handleChange('ownerDefault', 'family')}
                    disabled={isSaving}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Family</span>
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="ownerDefault"
                    value="estate"
                    checked={formData.ownerDefault === 'estate'}
                    onChange={() => handleChange('ownerDefault', 'estate')}
                    disabled={isSaving}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Estate</span>
                </label>
              </div>
            </div>

            {/* Priority */}
            <div>
              <label
                htmlFor="rule-priority"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Priority
              </label>
              <input
                id="rule-priority"
                type="number"
                min="1"
                max="100"
                value={formData.priority}
                onChange={(e) =>
                  handleChange('priority', parseInt(e.target.value, 10) || 5)
                }
                disabled={isSaving}
                className={clsx(
                  'w-24 px-3 py-2 text-sm border rounded-md',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  'disabled:bg-gray-50 disabled:cursor-not-allowed',
                  'border-gray-300'
                )}
                data-testid="rule-priority-input"
              />
              <p className="mt-1 text-xs text-gray-500">
                Higher priority rules are checked first (1-100)
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSaving}
              className={clsx(
                'px-4 py-2 text-sm font-medium rounded-md',
                'text-gray-700 hover:bg-gray-200',
                'focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              Cancel
            </button>
            {/* Create & Classify button - only shown when creating from transaction with onCreateAndClassify callback */}
            {isCreatingFromTransaction && onCreateAndClassify && (
              <button
                type="button"
                onClick={handleCreateAndClassify}
                disabled={!canSubmit}
                className={clsx(
                  'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md',
                  'bg-green-600 text-white hover:bg-green-700',
                  'focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
                data-testid="create-and-classify-button"
              >
                {isSaving ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <CheckCircle size={16} />
                )}
                {isSaving ? 'Saving...' : 'Create & Classify'}
              </button>
            )}
            <button
              type="submit"
              disabled={!canSubmit}
              className={clsx(
                'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md',
                'bg-blue-600 text-white hover:bg-blue-700',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
              data-testid="save-rule-button"
            >
              {isSaving ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Save size={16} />
              )}
              {isSaving ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Rule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RuleFormModal;
